import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  AcademicCapIcon,
  CheckCircleIcon,
  XMarkIcon,
  ClockIcon,
  EyeIcon,
  UserIcon,
  CalendarIcon,
  PhotoIcon,
  ChevronLeftIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import { API_BASE as API } from '../../lib/config';

const QualificationManagement = ({ onBack }) => {
  const [qualifications, setQualifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQualification, setSelectedQualification] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [stats, setStats] = useState({
    pending: 0,
    approved: 0,
    rejected: 0
  });
  const [filter, setFilter] = useState('pending');
  const [reviewComment, setReviewComment] = useState('');
  const prevPendingRef = useRef(0);
  const pollTimerRef = useRef(null);

  useEffect(() => {
    fetchQualifications();
    // start polling (30s)
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    pollTimerRef.current = setInterval(() => {
      fetchQualifications(true);
    }, 30000);
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [filter]);

  const fetchQualifications = async (isPolling = false) => {
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
      const url = filter ? `${API}/admin/qualifications?status=${filter}` : `${API}/admin/qualifications`;
      const response = await axios.get(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setQualifications(response.data);
      
      // 統計情報を更新
      const pendingCount = response.data.filter(q => q.status === 'pending').length;
      const approvedCount = response.data.filter(q => q.status === 'approved').length;
      const rejectedCount = response.data.filter(q => q.status === 'rejected').length;
      
      setStats({
        pending: pendingCount,
        approved: approvedCount,
        rejected: rejectedCount
      });
      if (isPolling && filter === 'pending' && pendingCount > (prevPendingRef.current || 0)) {
        toast.success('新しい資格申請が届きました');
      }
      prevPendingRef.current = pendingCount;
      
    } catch (error) {
      console.error('Failed to fetch qualifications:', error);
      toast.error('資格申請の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetail = async (qualificationId) => {
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/qualifications/${qualificationId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSelectedQualification(response.data);
      setShowDetailModal(true);
      setReviewComment('');
    } catch (error) {
      console.error('Failed to fetch qualification detail:', error);
      toast.error('詳細情報の取得に失敗しました');
    }
  };

  const handleReview = async (status) => {
    if (!selectedQualification) return;

    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
      await axios.put(`${API}/admin/qualifications/${selectedQualification.id}`, {
        status,
        rejection_reason: status === 'rejected' ? reviewComment : null
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success(status === 'approved' ? '資格を承認しました' : '資格を却下しました');
      setShowDetailModal(false);
      setSelectedQualification(null);
      setReviewComment('');
      fetchQualifications();
    } catch (error) {
      console.error('Failed to review qualification:', error);
      toast.error('資格の審査に失敗しました');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircleIcon className="w-4 h-4 mr-1" />
            承認済
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XMarkIcon className="w-4 h-4 mr-1" />
            却下
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <ClockIcon className="w-4 h-4 mr-1" />
            確認中
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={onBack}
              className="mr-4 p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">資格承認管理</h1>
              <p className="text-gray-600 mt-1">求職者から申請された資格を審査・管理します</p>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <ClockIcon className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">確認待ち</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.pending}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircleIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">承認済</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.approved}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <XMarkIcon className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">却下</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.rejected}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8" aria-label="Tabs">
            {[
              { key: 'pending', label: '確認待ち', count: stats.pending },
              { key: 'approved', label: '承認済', count: stats.approved },
              { key: 'rejected', label: '却下', count: stats.rejected }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  filter === tab.key
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
                {tab.count > 0 && (
                  <span className={`ml-2 py-0.5 px-2.5 rounded-full text-xs ${
                    filter === tab.key
                      ? 'bg-blue-100 text-blue-600'
                      : 'bg-gray-100 text-gray-900'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Qualifications List */}
        <div className="p-6">
          {qualifications.length === 0 ? (
            <div className="text-center py-12">
              <AcademicCapIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">該当する資格申請がありません</h3>
              <p className="text-gray-500">
                {filter === 'pending' && '新しい資格申請があると、ここに表示されます。'}
                {filter === 'approved' && '承認済みの資格申請がありません。'}
                {filter === 'rejected' && '却下された資格申請がありません。'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {qualifications.map((qualification) => (
                <div key={qualification.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <UserIcon className="h-5 w-5 text-gray-400 mr-2" />
                        <span className="font-medium text-gray-900">{qualification.user_name}</span>
                        <div className="ml-4">
                          {getStatusBadge(qualification.status)}
                        </div>
                      </div>
                      
                      <div className="flex items-center mb-2">
                        <AcademicCapIcon className="h-5 w-5 text-blue-600 mr-2" />
                        <span className="text-lg font-semibold text-gray-900">{qualification.qualification_name}</span>
                      </div>
                      
                      {qualification.obtained_date && (
                        <div className="flex items-center text-sm text-gray-600 mb-2">
                          <CalendarIcon className="h-4 w-4 mr-1" />
                          取得日: {new Date(qualification.obtained_date).toLocaleDateString('ja-JP')}
                        </div>
                      )}
                      
                      <div className="text-sm text-gray-500">
                        申請日時: {new Date(qualification.submitted_at).toLocaleString('ja-JP')}
                      </div>
                    </div>

                    <div className="flex space-x-2 ml-4">
                      <button
                        onClick={() => handleViewDetail(qualification.id)}
                        className="flex items-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                      >
                        <EyeIcon className="h-4 w-4 mr-1" />
                        詳細
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedQualification && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-0 py-4 flex items-center justify-between mb-6">
              <div className="flex items-center">
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="mr-4 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
                </button>
                <h2 className="text-xl font-semibold">資格詳細</h2>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* User Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">求職者情報</h4>
                <div className="grid grid-cols-1 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">求職者名:</span>
                    <span className="ml-2 font-medium">{selectedQualification.user_name}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">メールアドレス:</span>
                    <span className="ml-2 font-medium">{selectedQualification.user_email}</span>
                  </div>
                </div>
              </div>

              {/* Qualification Info */}
              <div>
                <h4 className="font-medium text-gray-900 mb-4">資格情報</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">資格名</label>
                    <p className="text-gray-900">{selectedQualification.qualification_name}</p>
                  </div>

                  {selectedQualification.obtained_date && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">取得日</label>
                      <p className="text-gray-900">{new Date(selectedQualification.obtained_date).toLocaleDateString('ja-JP')}</p>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">申請日時</label>
                    <p className="text-gray-900">{new Date(selectedQualification.submitted_at).toLocaleString('ja-JP')}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ステータス</label>
                    {getStatusBadge(selectedQualification.status)}
                  </div>

                  {/* Certificate Image */}
                  {selectedQualification.qualification_image && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">提出された資格証明書画像</label>
                      <div className="border border-gray-200 rounded-lg p-4">
                        <img
                          src={selectedQualification.qualification_image}
                          alt="資格証明書"
                          className="w-full rounded-lg cursor-pointer hover:opacity-90"
                          onClick={() => window.open(selectedQualification.qualification_image, '_blank')}
                        />
                        <p className="text-xs text-gray-500 mt-2 text-center">
                          クリックで拡大表示
                        </p>
                      </div>
                    </div>
                  )}

                  {selectedQualification.status === 'rejected' && selectedQualification.rejection_reason && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">却下理由</label>
                      <p className="text-red-600 bg-red-50 p-3 rounded-lg">{selectedQualification.rejection_reason}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Review Section - Only show for pending qualifications */}
              {selectedQualification.status === 'pending' && (
                <div className="border-t pt-6">
                  <h4 className="font-medium text-gray-900 mb-4">審査</h4>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        コメント（却下時のみ）
                      </label>
                      <textarea
                        value={reviewComment}
                        onChange={(e) => setReviewComment(e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="却下理由を入力してください..."
                      />
                    </div>

                    <div className="flex gap-4">
                      <button
                        onClick={() => handleReview('approved')}
                        className="flex-1 bg-green-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center justify-center"
                      >
                        <CheckCircleIcon className="h-5 w-5 mr-2" />
                        承認する
                      </button>
                      <button
                        onClick={() => handleReview('rejected')}
                        className="flex-1 bg-red-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center justify-center"
                      >
                        <XMarkIcon className="h-5 w-5 mr-2" />
                        却下する
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Previous Review Info */}
              {selectedQualification.status !== 'pending' && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">審査結果</h4>
                  <div className="text-sm space-y-1">
                    <div>
                      <span className="text-gray-500">ステータス:</span>
                      <span className="ml-2">
                        {getStatusBadge(selectedQualification.status)}
                      </span>
                    </div>
                    {selectedQualification.reviewed_at && (
                      <div>
                        <span className="text-gray-500">審査日時:</span>
                        <span className="ml-2 font-medium">
                          {new Date(selectedQualification.reviewed_at).toLocaleString('ja-JP')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QualificationManagement;